import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Tooltip,
  Alert,
  Snackbar,
  Checkbox,
  Pagination,
  FormControlLabel,
} from '@mui/material';
import {
  Search,
  FilterList,
  FileDownload,
  Add,
  Edit,
  Delete,
  MoreVert,
  Person,
  Email,
  Phone,
  Business,
  Work,
  Visibility,
  Block,
  CheckCircle,
  Update,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';

interface User {
  id: number;
  name: string;
  email: string;
  phone: string;
  role: string;
  module_access: string;
  status: 'active' | 'inactive' | 'suspended';
  avatar?: string;
  department?: string;
  position?: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const navigate = useNavigate();

  // Mock data matching the design exactly
  const mockUsers: User[] = [
    { id: 1, name: 'Liam Anderson', email: 'example@gmail.com', phone: '000 0000 0000', role: 'Admin', module_access: 'Customer, Suppliers, Invoices', status: 'active' },
    { id: 2, name: 'Noah Williams', email: 'example@gmail.com', phone: '111 1111 1111', role: 'Admin', module_access: 'Customer, Suppliers, Invoices', status: 'active' },
    { id: 3, name: 'Sofia Martinez', email: 'example@gmail.com', phone: '222 2222 2222', role: 'Admin', module_access: 'Customer, Suppliers, Invoices', status: 'active' },
    { id: 4, name: 'Lucas Silva', email: 'example@gmail.com', phone: '333 3333 3333', role: 'Admin', module_access: 'Customer, Suppliers, Invoices', status: 'active' },
    { id: 5, name: 'Ethan Brown', email: 'example@gmail.com', phone: '444 4444 4444', role: 'Admin', module_access: 'Customer, Suppliers, Invoices', status: 'active' },
    { id: 6, name: 'Oliver Garcia', email: 'example@gmail.com', phone: '555 5555 5555', role: 'Admin', module_access: 'Customer, Suppliers, Invoices', status: 'active' },
    { id: 7, name: 'James Müller', email: 'example@gmail.com', phone: '666 6666 6666', role: 'Admin', module_access: 'Customer, Suppliers, Invoices', status: 'active' },
    { id: 8, name: 'Mia Johansson', email: 'example@gmail.com', phone: '777 7777 7777', role: 'Admin', module_access: 'Customer, Suppliers, Invoices', status: 'active' },
    { id: 9, name: 'Daniel Rossi', email: 'example@gmail.com', phone: '888 8888 8888', role: 'Admin', module_access: 'Customer, Suppliers, Invoices', status: 'active' },
    { id: 10, name: 'Daniel Rossi', email: 'example@gmail.com', phone: '999 9999 9999', role: 'Admin', module_access: 'Customer, Suppliers, Invoices', status: 'active' },
  ];

  useEffect(() => {
    setUsers(mockUsers);
    setTotalRecords(60); // As shown in design
    setTotalPages(6); // 60 records / 10 per page
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;

    try {
      await apiService.deleteUser(selectedUser.id);
      setSnackbar({ open: true, message: 'User deleted successfully', severity: 'success' });
      // Remove from mock data
      setUsers(users.filter(user => user.id !== selectedUser.id));
    } catch (error) {
      console.error('Error deleting user:', error);
      setSnackbar({ open: true, message: 'Failed to delete user', severity: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    }
  };

  const handleStatusChange = async (userId: number, newStatus: string) => {
    try {
      await apiService.updateUserStatus(userId, newStatus);
      setSnackbar({ open: true, message: 'User status updated successfully', severity: 'success' });
      // Update mock data
      setUsers(users.map(user => 
        user.id === userId ? { ...user, status: newStatus as any } : user
      ));
    } catch (error) {
      console.error('Error updating user status:', error);
      setSnackbar({ open: true, message: 'Failed to update user status', severity: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(users.map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId: number, checked: boolean) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (event: any) => {
    setItemsPerPage(event.target.value);
  };

  return (
    <Box sx={{ 
      p: 3,
      backgroundColor: '#f0f9ff' // Light blue background as shown in design
    }}>
        <Card sx={{ 
          borderRadius: '12px', 
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          backgroundColor: 'white'
        }}>
          <CardContent sx={{ p: 3 }}>
            {/* Top Controls */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 3,
              gap: 2
            }}>
              <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                <TextField
                  placeholder="Search here"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      '& fieldset': {
                        borderColor: '#d1d5db',
                      },
                      '&:hover fieldset': {
                        borderColor: '#9ca3af',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3b82f6',
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: '#6b7280' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  sx={{
                    borderRadius: '8px',
                    borderColor: '#3b82f6',
                    color: '#3b82f6',
                    '&:hover': {
                      borderColor: '#2563eb',
                      backgroundColor: '#eff6ff',
                    },
                    px: 3
                  }}
                >
                  Filter
                </Button>
              </Box>

              <Button
                variant="outlined"
                startIcon={<FileDownload />}
                sx={{
                  borderRadius: '8px',
                  borderColor: '#3b82f6',
                  color: '#3b82f6',
                  '&:hover': {
                    borderColor: '#2563eb',
                    backgroundColor: '#eff6ff',
                  },
                  px: 3
                }}
              >
                Export
              </Button>
            </Box>

            {/* Data Table */}
            <TableContainer component={Paper} sx={{ 
              borderRadius: '8px',
              boxShadow: 'none',
              border: '1px solid #e5e7eb'
            }}>
              <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#0d9488' }}> {/* Teal background for headers */}
                <TableCell sx={{ 
                  fontWeight: 600, 
                  color: 'white', // White text on teal background
                  borderBottom: '1px solid #e5e7eb',
                  py: 2
                }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                          checked={selectedUsers.length === users.length && users.length > 0}
                          indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          sx={{ 
                            color: '#3b82f6',
                            '&.Mui-checked': {
                              color: '#3b82f6',
                            },
                          }}
                        />
                        <Box sx={{ 
                          width: 8, 
                          height: 8, 
                          backgroundColor: '#3b82f6', 
                          borderRadius: '2px' 
                        }} />
                        User Full Name
                      </Box>
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      color: 'white', // White text on teal background
                      borderBottom: '1px solid #e5e7eb',
                      py: 2
                    }}>
                      Role
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      color: 'white', // White text on teal background
                      borderBottom: '1px solid #e5e7eb',
                      py: 2
                    }}>
                      Email Address
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      color: 'white', // White text on teal background
                      borderBottom: '1px solid #e5e7eb',
                      py: 2
                    }}>
                      Phone Number
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      color: 'white', // White text on teal background
                      borderBottom: '1px solid #e5e7eb',
                      py: 2
                    }}>
                      Module Access
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      color: 'white', // White text on teal background
                      borderBottom: '1px solid #e5e7eb',
                      py: 2
                    }}>
                      {/* Update button column header */}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} sx={{ 
                      '&:hover': { backgroundColor: '#f9fafb' },
                      borderBottom: '1px solid #f3f4f6'
                    }}>
                      <TableCell sx={{ py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox
                            checked={selectedUsers.includes(user.id)}
                            onChange={(e) => handleSelectUser(user.id, e.target.checked)}
                            sx={{ 
                              color: '#3b82f6',
                              '&.Mui-checked': {
                                color: '#3b82f6',
                              },
                            }}
                          />
                          <Typography variant="body2" sx={{ color: '#374151', fontWeight: 500 }}>
                            {user.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Chip
                          label={user.role}
                          size="small"
                          sx={{
                            backgroundColor: '#f3f4f6',
                            color: '#374151',
                            fontWeight: 500,
                            fontSize: '0.75rem'
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          {user.email}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          {user.phone}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          {user.module_access}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Update />}
                          sx={{
                            borderRadius: '6px',
                            borderColor: '#0d9488', // Teal color to match design
                            color: '#0d9488', // Teal color to match design
                            fontSize: '0.75rem',
                            px: 2,
                            py: 0.5,
                            '&:hover': {
                              borderColor: '#0f766e',
                              backgroundColor: '#f0fdfa',
                            },
                          }}
                        >
                          Update
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Footer */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mt: 3,
              pt: 2,
              borderTop: '1px solid #e5e7eb'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Showing
                </Typography>
                <FormControl size="small" sx={{ minWidth: 60 }}>
                  <Select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#d1d5db',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#9ca3af',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3b82f6',
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Showing 1 to 10 out of 60 records
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={currentPage === 1}
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '6px',
                    borderColor: '#d1d5db',
                    color: '#6b7280',
                    '&:hover': {
                      borderColor: '#9ca3af',
                      backgroundColor: '#f9fafb',
                    },
                    '&:disabled': {
                      borderColor: '#e5e7eb',
                      color: '#d1d5db',
                    },
                  }}
                >
                  ←
                </Button>
                
                {[1, 2, 3, 4].map((page) => (
                  <Button
                    key={page}
                    variant={currentPage === page ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setCurrentPage(page)}
                    sx={{
                      minWidth: 32,
                      height: 32,
                      borderRadius: '6px',
                      borderColor: '#d1d5db',
                      color: currentPage === page ? 'white' : '#6b7280',
                      backgroundColor: currentPage === page ? '#0d9488' : 'transparent', // Teal for active page
                      '&:hover': {
                        borderColor: '#9ca3af',
                        backgroundColor: currentPage === page ? '#0f766e' : '#f9fafb', // Darker teal on hover
                      },
                    }}
                  >
                    {page}
                  </Button>
                ))}
                
                <Button
                  variant="outlined"
                  size="small"
                  disabled={currentPage === totalPages}
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '6px',
                    borderColor: '#d1d5db',
                    color: '#6b7280',
                    '&:hover': {
                      borderColor: '#9ca3af',
                      backgroundColor: '#f9fafb',
                    },
                    '&:disabled': {
                      borderColor: '#e5e7eb',
                      color: '#d1d5db',
                    },
                  }}
                >
                  →
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{selectedUser?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;